// Need to use the React-specific entry point to import createApi
import type {
  BulkActivateUserRequest,
  ConvertUserTypeRequest,
  ExtendBadgeExpirationRequest,
  FileResponse,
  GetListResponseOfPersonnelSimpleResponse,
  GetListResponseOfSelectOption,
  GetPersonnelListRequest,
  GetPersonnelListRequestV2,
  GetPersonnelSelectOptionsRequest,
  PersonnelImportColumnResponse,
  PersonnelImportPreviewRequest,
  PersonnelImportPreviewResponse,
  PersonnelImportRequest,
  PersonnelPermissionResponse,
  PersonnelRequest,
  PersonnelResponse,
  SesSuppressionResponse,
  SetBadgeExpirationRequest,
} from 'src/api/ProactApiClient';
import {personnelApiClient} from 'src/api/ProactApiClientInstances';
import {
  personnelSelectOptionsQueryFn,
  personQueryFn,
} from 'src/app/forms/offline/reads';
import {PERSONNEL_SECTION_SOURCES} from 'src/offline/personnelSources';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Define a service using a base URL and expected endpoints
export const personnelApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getPerson: builder.query<PersonnelResponse, number>({
      queryFn: personQueryFn(
        id => personnelApiClient.details(id),
        PERSONNEL_SECTION_SOURCES
      ),
      providesTags: ['Personnel'],
    }),
    getPersonnelSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetPersonnelSelectOptionsRequest
    >({
      queryFn: personnelSelectOptionsQueryFn(
        params => personnelApiClient.listSelectOptions(params),
        PERSONNEL_SECTION_SOURCES
      ),
      providesTags: ['Personnel'],
    }),
    getPersonnelList: builder.query<
      GetListResponseOfPersonnelSimpleResponse,
      GetPersonnelListRequest
    >({
      queryFn: clientQuery(params => personnelApiClient.list(params)),
      providesTags: ['Personnel'],
    }),
    getPersonnelListV2: builder.query<
      GetListResponseOfPersonnelSimpleResponse,
      GetPersonnelListRequestV2
    >({
      queryFn: clientQuery(params => personnelApiClient.listV2(params)),
      providesTags: ['Personnel'],
    }),
    getPersonPermissions: builder.query<PersonnelPermissionResponse, number>({
      queryFn: clientQuery(id => personnelApiClient.getPermissions(id)),
      providesTags: ['Personnel'],
    }),
    getPersonnelImportColumns: builder.query<
      PersonnelImportColumnResponse[],
      void
    >({
      queryFn: clientQuery(() => personnelApiClient.getImportColumns()),
    }),
    getSesSuppressionStatus: builder.query<SesSuppressionResponse, number>({
      queryFn: clientQuery(id =>
        personnelApiClient.getSesSuppressionStatus(id)
      ),
      providesTags: ['Personnel'],
    }),
    previewPersonnelImport: builder.mutation<
      PersonnelImportPreviewResponse,
      PersonnelImportPreviewRequest
    >({
      queryFn: clientQuery(request => personnelApiClient.previewImport(request)),
    }),
    importPersonnel: builder.mutation<string, PersonnelImportRequest>({
      queryFn: clientQuery(request => personnelApiClient.import(request)),
      invalidatesTags: ['Personnel'],
    }),
    createPerson: builder.mutation<PersonnelResponse, PersonnelRequest>({
      queryFn: clientQuery(model => personnelApiClient.create(model)),
      invalidatesTags: ['Personnel'],
    }),
    updatePerson: builder.mutation<
      PersonnelResponse,
      {id: number; model: PersonnelRequest}
    >({
      queryFn: clientQuery(({id, model}) =>
        personnelApiClient.update(id, model)
      ),
      // Saving personnel can enqueue a badge regeneration job, so the badge
      // history/detail cache must be busted alongside the personnel cache.
      invalidatesTags: (_result, _error, {id}) => [
        'Personnel',
        {type: 'PersonnelBadges', id: `LIST-${id}`},
      ],
    }),
    deletePerson: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => personnelApiClient.delete(id)),
      invalidatesTags: ['Personnel'],
    }),
    exportPersonnel: builder.mutation<
      FileResponse | null,
      GetPersonnelListRequestV2
    >({
      queryFn: clientQuery(request => personnelApiClient.export(request)),
    }),
    convertUserType: builder.mutation<
      PersonnelResponse,
      {id: number; model: ConvertUserTypeRequest}
    >({
      queryFn: clientQuery(({id, model}) =>
        personnelApiClient.convertUserType(id, model)
      ),
      // Type conversion rewrites badge-affecting fields (VendorId/EmployeeId)
      // and can enqueue a badge regeneration job.
      invalidatesTags: (_result, _error, {id}) => [
        'Personnel',
        {type: 'PersonnelBadges', id: `LIST-${id}`},
      ],
    }),
    extendBadgeExpiration: builder.mutation<
      PersonnelResponse,
      {id: number; model: ExtendBadgeExpirationRequest}
    >({
      queryFn: clientQuery(({id, model}) =>
        personnelApiClient.extendBadgeExpiration(id, model)
      ),
      invalidatesTags: ['Personnel'],
    }),
    setBadgeExpiration: builder.mutation<
      PersonnelResponse,
      {id: number; model: SetBadgeExpirationRequest}
    >({
      queryFn: clientQuery(({id, model}) =>
        personnelApiClient.setBadgeExpiration(id, model)
      ),
      invalidatesTags: ['Personnel'],
    }),
    activateUser: builder.mutation<PersonnelResponse, number>({
      queryFn: clientQuery(id => personnelApiClient.activateUser(id)),
      invalidatesTags: ['Personnel'],
    }),
    bulkActivateUser: builder.mutation<boolean, BulkActivateUserRequest>({
      queryFn: clientQuery(request =>
        personnelApiClient.bulkActivateUser(request)
      ),
      invalidatesTags: ['Personnel'],
    }),
    lockUser: builder.mutation<PersonnelResponse, number>({
      queryFn: clientQuery(id => personnelApiClient.lockUser(id)),
      invalidatesTags: ['Personnel'],
    }),
    deactivateUser: builder.mutation<PersonnelResponse, number>({
      queryFn: clientQuery(id => personnelApiClient.deactivateUser(id)),
      invalidatesTags: ['Personnel'],
    }),
    syncCognitoUser: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => personnelApiClient.syncCognitoUser(id)),
      invalidatesTags: ['Personnel'],
    }),
    resolveEmailMismatch: builder.mutation<PersonnelResponse, number>({
      queryFn: clientQuery(id => personnelApiClient.resolveEmailMismatch(id)),
      invalidatesTags: ['Personnel'],
    }),
    removeSesSuppression: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => personnelApiClient.removeSesSuppression(id)),
      invalidatesTags: ['Personnel'],
    }),
    disableMfa: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => personnelApiClient.disableMfa(id)),
      invalidatesTags: ['Personnel'],
    }),
    enableNotificationEmails: builder.mutation<PersonnelResponse, number>({
      queryFn: clientQuery(id => personnelApiClient.enableNotificationEmails(id)),
      invalidatesTags: ['Personnel'],
    }),
    disableNotificationEmails: builder.mutation<PersonnelResponse, number>({
      queryFn: clientQuery(id =>
        personnelApiClient.disableNotificationEmails(id)
      ),
      invalidatesTags: ['Personnel'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPersonnelListQuery,
  useGetPersonnelSelectOptionsQuery,
  useGetPersonnelListV2Query,
  useGetPersonQuery,
  useGetPersonPermissionsQuery,
  useGetSesSuppressionStatusQuery,
  useGetPersonnelImportColumnsQuery,
  usePreviewPersonnelImportMutation,
  useImportPersonnelMutation,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useExportPersonnelMutation,
  useConvertUserTypeMutation,
  useExtendBadgeExpirationMutation,
  useSetBadgeExpirationMutation,
  useActivateUserMutation,
  useBulkActivateUserMutation,
  useLockUserMutation,
  useDeactivateUserMutation,
  useSyncCognitoUserMutation,
  useResolveEmailMismatchMutation,
  useRemoveSesSuppressionMutation,
  useEnableNotificationEmailsMutation,
  useDisableNotificationEmailsMutation,
  useDisableMfaMutation,
} = personnelApi;
